import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent } from "@/shared/components/ui/card";
import { 
  Shield, 
  Zap, 
  Globe, 
  Lock, 
  ArrowRight, 
  CheckCircle2,
  CreditCard,
  Building2,
  BarChart3,
  Clock,
  Headphones,
  ChevronRight
} from "lucide-react";

const features = [
  {
    icon: Zap,
    title: "Lightning Fast",
    description: "Process transactions in milliseconds with our optimized infrastructure"
  },
  {
    icon: Shield,
    title: "Bank-Grade Security",
    description: "PCI DSS Level 1 compliant with end-to-end encryption"
  },
  {
    icon: Globe,
    title: "Global Coverage",
    description: "Accept payments from 180+ countries with local payment methods"
  },
  {
    icon: BarChart3,
    title: "Real-time Analytics",
    description: "Monitor your transactions with comprehensive dashboards"
  },
  {
    icon: Clock,
    title: "99.99% Uptime",
    description: "Enterprise-grade reliability you can count on"
  },
  {
    icon: Headphones,
    title: "24/7 Support",
    description: "Dedicated support team available around the clock"
  }
];

const stats = [
  { value: "$2B+", label: "Processed Monthly" },
  { value: "99.99%", label: "Uptime" },
  { value: "180+", label: "Countries" },
  { value: "<50ms", label: "Latency" }
];

export default function HomeApp() {
  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-x-hidden">
      <style>{`
        .gradient-text {
          background: linear-gradient(135deg, #60a5fa 0%, #a78bfa 50%, #f472b6 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .hero-gradient {
          background: radial-gradient(ellipse 80% 50% at 50% -20%, rgba(120, 119, 198, 0.3), transparent);
        }
        
        .card-glow:hover {
          box-shadow: 0 0 40px rgba(96, 165, 250, 0.15);
        }
        
        .grid-pattern {
          background-image: 
            linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
          background-size: 60px 60px;
        }
      `}</style>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-slate-950/80 border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center">
                <Shield className="h-5 w-5 text-white" />
              </div>
              <span className="font-bold text-xl tracking-tight">Demo Gateway</span>
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-400 hover:text-white transition-colors text-sm">Features</a>
              <a href="#pricing" className="text-slate-400 hover:text-white transition-colors text-sm">Pricing</a>
              <a href="#docs" className="text-slate-400 hover:text-white transition-colors text-sm">Documentation</a>
              <a href="#contact" className="text-slate-400 hover:text-white transition-colors text-sm">Contact</a>
            </div>
            
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                className="text-slate-400 hover:text-white"
                onClick={() => window.location.href = 'https://panel-demo-gateway.e-volvo.io'}
              >
                Sign In
              </Button>
              <Button 
                className="bg-gradient-to-r from-blue-500 to-violet-600 hover:from-blue-600 hover:to-violet-700 text-white"
                onClick={() => window.location.href = 'https://panel-demo-gateway.e-volvo.io'}
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 hero-gradient">
        <div className="absolute inset-0 grid-pattern" />
        <div className="max-w-7xl mx-auto relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/50 border border-slate-700/50 mb-8">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-sm text-slate-400">Now processing $2B+ monthly</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              The Payment Gateway
              <br />
              <span className="gradient-text">Built for Scale</span>
            </h1>
            
            <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto">
              Accept payments globally with enterprise-grade security, lightning-fast processing, 
              and comprehensive tools for engines, resellers, and merchants.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-blue-500 to-violet-600 hover:from-blue-600 hover:to-violet-700 text-white px-8 py-6 text-lg rounded-xl"
                onClick={() => window.location.href = 'https://panel-demo-gateway.e-volvo.io'}
              >
                Start Processing
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-slate-700 text-slate-300 hover:bg-slate-800 px-8 py-6 text-lg rounded-xl"
              >
                View Documentation
              </Button>
            </div>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-20"
          >
            {stats.map((stat, index) => (
              <div key={index} className="text-center p-6 rounded-2xl bg-slate-900/50 border border-slate-800/50">
                <div className="text-3xl md:text-4xl font-bold gradient-text mb-2">{stat.value}</div>
                <div className="text-slate-500 text-sm">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">
              Everything You Need to
              <span className="gradient-text"> Accept Payments</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Built from the ground up for high-volume payment processing with the tools and 
              flexibility modern businesses demand.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card className="bg-slate-900/50 border-slate-800/50 hover:border-slate-700 transition-all duration-300 card-glow h-full">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-violet-500/20 flex items-center justify-center mb-4">
                      <feature.icon className="h-6 w-6 text-blue-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                    <p className="text-slate-400 text-sm">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* User Types Section */}
      <section className="py-20 px-4 bg-slate-900/30">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">
              Built for <span className="gradient-text">Every Business Model</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Whether you're an engine operator, reseller, or merchant, we have the tools you need.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Zap,
                title: "Engines",
                description: "Full payment infrastructure management with multi-tenant support, provider routing, and comprehensive analytics.",
                features: ["Multi-tenant architecture", "Provider management", "Real-time monitoring", "Custom routing rules"],
                color: "violet"
              },
              {
                icon: Building2,
                title: "Resellers",
                description: "White-label solutions for partners looking to offer payment services under their own brand.",
                features: ["White-label branding", "Merchant management", "Commission tracking", "Dedicated support"],
                color: "emerald"
              },
              {
                icon: CreditCard,
                title: "Merchants",
                description: "Simple integration for businesses of all sizes with powerful tools to manage your payments.",
                features: ["Easy API integration", "Multiple payment methods", "Instant settlements", "Fraud protection"],
                color: "amber"
              }
            ].map((type, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card className="bg-slate-900/50 border-slate-800/50 hover:border-slate-700 transition-all duration-300 h-full">
                  <CardContent className="p-8">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${
                      type.color === 'violet' ? 'from-violet-500/20 to-purple-500/20' :
                      type.color === 'emerald' ? 'from-emerald-500/20 to-teal-500/20' :
                      'from-amber-500/20 to-orange-500/20'
                    } flex items-center justify-center mb-6`}>
                      <type.icon className={`h-7 w-7 ${
                        type.color === 'violet' ? 'text-violet-400' :
                        type.color === 'emerald' ? 'text-emerald-400' :
                        'text-amber-400'
                      }`} />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-3">{type.title}</h3>
                    <p className="text-slate-400 mb-6">{type.description}</p>
                    <ul className="space-y-3">
                      {type.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-slate-300 text-sm">
                          <CheckCircle2 className={`h-4 w-4 ${
                            type.color === 'violet' ? 'text-violet-400' :
                            type.color === 'emerald' ? 'text-emerald-400' :
                            'text-amber-400'
                          }`} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button 
                      className="w-full mt-6 bg-slate-800 hover:bg-slate-700 text-white"
                      onClick={() => window.location.href = 'https://panel-demo-gateway.e-volvo.io'}
                    >
                      Get Started
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative rounded-3xl overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-violet-600" />
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnYtMmg0djJoMnY0aC0ydjJoLTR2LTJ6bTAtOGgtMnYtMmgydjJ6bTAgMTBoLTJ2LTJoMnYyem0tMTAtNGgtMnYtMmgydjJ6bTAgNGgtMnYtMmgydjJ6bTAtOGgtMnYtMmgydjJ6bTEwIDhoLTJ2LTJoMnYyem0xMC00aC0ydi0yaDJ2MnptMCA0aC0ydi0yaDJ2MnptMC04aC0ydi0yaDJ2MnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30" />
            <div className="relative px-8 py-16 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Ready to Transform Your Payments?
              </h2>
              <p className="text-blue-100 mb-8 max-w-xl mx-auto">
                Join thousands of businesses already processing payments with Demo Gateway. 
                Get started in minutes.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button 
                  size="lg"
                  className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg rounded-xl"
                  onClick={() => window.location.href = 'https://panel-demo-gateway.e-volvo.io'}
                >
                  Create Account
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg rounded-xl"
                >
                  Talk to Sales
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800/50 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center">
                  <Shield className="h-5 w-5 text-white" />
                </div>
                <span className="font-bold text-xl">Demo Gateway</span>
              </div>
              <p className="text-slate-500 text-sm">
                Enterprise payment gateway for the modern era.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-slate-500 hover:text-white text-sm transition-colors">Features</a></li>
                <li><a href="#" className="text-slate-500 hover:text-white text-sm transition-colors">Pricing</a></li>
                <li><a href="#" className="text-slate-500 hover:text-white text-sm transition-colors">API Documentation</a></li>
                <li><a href="#" className="text-slate-500 hover:text-white text-sm transition-colors">Integrations</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-slate-500 hover:text-white text-sm transition-colors">About Us</a></li>
                <li><a href="#" className="text-slate-500 hover:text-white text-sm transition-colors">Careers</a></li>
                <li><a href="#" className="text-slate-500 hover:text-white text-sm transition-colors">Blog</a></li>
                <li><a href="#" className="text-slate-500 hover:text-white text-sm transition-colors">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-slate-500 hover:text-white text-sm transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="text-slate-500 hover:text-white text-sm transition-colors">Terms of Service</a></li>
                <li><a href="#" className="text-slate-500 hover:text-white text-sm transition-colors">Compliance</a></li>
                <li><a href="#" className="text-slate-500 hover:text-white text-sm transition-colors">Security</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800/50 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} Demo Gateway. All rights reserved.
            </p>
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-slate-600" />
              <span className="text-slate-500 text-sm">PCI DSS Level 1 Compliant</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

